// Libs
import classNames from 'classnames/bind';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { UniqueIdentifier } from '@dnd-kit/core';
import { useFormContext, useWatch } from 'react-hook-form';
import { MouseEvent, useCallback, useState } from 'react';
import { Avatar, Box, Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
// Components, Layouts, Pages
import { BaseDynamicSelect, BaseSelect, UnderDevelopment } from '~/components/common';
// Others
import { getListUser } from '~/thunks/user/userThunk';
import { getListTruck } from '~/thunks/truck/truckThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListTrailer } from '~/thunks/trailer/trailerThunk';
import { IQueryParamUser } from '~/utils/interface/user';
import { IQueryParamTrailer } from '~/utils/interface/trailer';
import { formatTitleCase, getFullName } from '~/utils/helper/common';
import { IOptionTruck, IQueryParamTruck } from '~/utils/interface/truck';
import { ProductDesignationEnum, RoleEnum } from '~/utils/enum/common';
import { IFormSliptOrder, IFormSplitOrder, IOrderSplit, ISettingSplitOrder } from '~/utils/interface/order';
import {
  ColumnTypeKanban,
  IBaseKanban,
  IBaseOption,
  IPayloadCallApiFn,
  IResultCallApiFn,
} from '~/utils/interface/common';
import {
  EMPTY_STRING,
  DEFAULT_LIMIT_PAGE,
  optionProductDesignation,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './CustomHeaderColumn.module.scss';
import { icons } from '~/assets';

type Props<T extends IBaseKanban> = {
  children: React.ReactNode;
  label: string;
  index: number;
  id: UniqueIdentifier;
  data: ColumnTypeKanban<T>;
  orderSplit?: IOrderSplit;
  onApplySettings?: (data: ISettingSplitOrder) => void;
  onDelete?: (index: number) => void;
};

const cx = classNames.bind(styles);

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const CustomHeaderColumn = <T extends IBaseKanban>(props: Props<T>) => {
  //#region Destructuring Props
  const { orderSplit, children, index, data, id, onDelete } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();
  const { attributes, setNodeRef, transition, transform } = useSortable({
    id,
    data: {
      type: 'container',
      children: data,
    },
    animateLayoutChanges,
  });

  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<IFormSliptOrder>();

  const { deleteJobIds } = watch();

  const trailerType = useWatch({
    control,
    name: `jobs.${index}.trailerType`,
    defaultValue: [],
  });

  const jobItems = useWatch({
    control,
    name: `jobItems`,
    defaultValue: [],
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [tempSettings, setTempSettings] = useState<IFormSplitOrder>({
    foremanId: EMPTY_STRING,
    crewIds: [],
    truckId: EMPTY_STRING,
    driverId: EMPTY_STRING,
    loaderId: [],
    trailerId: EMPTY_STRING,
    trailerType: [],
    orderItemIds: [],
    title: EMPTY_STRING,
    type: EMPTY_STRING,
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleShowMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    const currentJob = getValues(`jobs.${index}`);
    setTempSettings(currentJob);
  };

  const handleApplySettings = () => {
    setValue(`jobs.${index}`, tempSettings, { shouldValidate: true });

    handleCloseMenu();
  };

  const fetchOptionForeman = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.FOREMAN,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses?.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionCrew = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.CREW,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses?.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionDriver = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.DRIVER,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionTrailer = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamTrailer = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        trailerType,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListTrailer(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses?.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    [trailerType]
  );

  const fetchOptionTruck = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionTruck>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamTruck = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListTruck(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions = responses?.map((item) => ({
        value: item?.id,
        label: item?.name,
        trailerType: item?.trailerType,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionLoader = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      role: RoleEnum.LOADER,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses?.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionProductDesignation,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleClear = () => {
    const empty: IFormSplitOrder = {
      id: getValues(`jobs.${index}.id`),
      title: getValues(`jobs.${index}.title`),
      isDefault: getValues(`jobs.${index}.isDefault`),
      foremanId: EMPTY_STRING,
      crewIds: [],
      truckId: EMPTY_STRING,
      trailerId: EMPTY_STRING,
      loaderId: [],
      trailerType: [],
      orderItemIds: [],
    };

    setValue(`jobs.${index}`, empty);

    handleCloseMenu();
  };

  const handleDelete = () => {
    onDelete?.(index);

    if (jobItems?.[index]?.id && jobItems?.[index]?.id !== ProductDesignationEnum.UNASSIGNED)
      setValue('deleteJobIds', [...(deleteJobIds || []), String(jobItems?.[index]?.id)]);
  };
  //#endregion Handle Function

  return (
    <Stack
      direction={'column'}
      style={{ transition, transform: CSS.Translate.toString(transform) }}
      {...attributes}
      ref={setNodeRef}
      className={cx('container')}
    >
      {jobItems?.[index]?.items?.length === 0 && (
        <img
          onClick={handleDelete}
          className={cx('iconDelete')}
          src={icons.commonIconCloseRed}
          alt={t('common_alt_img', { ns: 'common/base' })}
        />
      )}
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} className={cx('headerColumn')}>
        <Typography className={cx('titleColumn')} component='div' variant='subtitle1' fontWeight='bold'>
          {data.name}
        </Typography>

        {jobItems?.[index]?.id !== ProductDesignationEnum.UNASSIGNED && (
          <IconButton
            aria-controls={open ? 'menuApplySettings' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleShowMenu}
          >
            <Avatar
              className={cx('iconSetting')}
              src={icons.commonIconSettingBlack}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </IconButton>
        )}
      </Stack>

      <Popover
        id='menuApplySettings'
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            className: cx('listMenu'),
          },
        }}
      >
        <Box component={'form'}>
          <Box className={cx('menuContent')}>
            <BaseSelect
              gap={10}
              height={37}
              borderRadius={8}
              styleType='contained'
              label={t('menu_apply_settings_label_type')}
              callApiFn={fetchOptionsType}
              value={tempSettings?.type}
              onChange={(data) => {
                setTempSettings((prev) => ({
                  ...prev,
                  type: String(data?.value),
                }));
              }}
              itemNameDefault={formatTitleCase(orderSplit?.jobs[index]?.type)}
            />

            <BaseSelect
              isSearch
              type='loadMore'
              gap={8}
              height={37}
              borderRadius={8}
              itemNameDefault={getFullName({
                firstName: orderSplit?.jobs[index]?.foreman?.firstName,
                lastName: orderSplit?.jobs[index]?.foreman?.lastName,
              })}
              styleType='contained'
              value={tempSettings?.foremanId}
              onChange={(data) => {
                setTempSettings((prev) => ({
                  ...prev,
                  foremanId: String(data?.value),
                }));
              }}
              label={t('menu_apply_settings_label_foreman')}
              callApiFn={fetchOptionForeman}
              errorMessage={errors.jobs?.[index]?.foremanId?.message}
            />

            <BaseDynamicSelect
              isSearch
              multiple={true}
              gap={8}
              borderRadius={8}
              label={t('menu_apply_settings_label_crew')}
              callApiFn={fetchOptionCrew}
              value={tempSettings?.crewIds || []}
              onChange={(selected) => {
                setTempSettings((prev) => ({
                  ...prev,
                  crewIds: selected?.optionsSelected?.map((opt) => opt.value),
                }));
              }}
              itemNameDefault={orderSplit?.jobs[index]?.crews?.map((item) =>
                getFullName({
                  firstName: item.firstName,
                  lastName: item.lastName,
                })
              )}
              errorMessage={errors.jobs?.[index]?.crewIds?.message}
            />

            <BaseSelect
              isSearch
              type='loadMore'
              gap={8}
              height={37}
              borderRadius={8}
              value={tempSettings?.driverId}
              itemNameDefault={getFullName({
                firstName: orderSplit?.jobs[index]?.driver?.firstName,
                lastName: orderSplit?.jobs[index]?.driver?.lastName,
              })}
              onChange={(data) => {
                setTempSettings((prev) => ({
                  ...prev,
                  driverId: String(data?.value),
                }));
              }}
              styleType='contained'
              label={t('menu_apply_settings_label_driver')}
              callApiFn={fetchOptionDriver}
              errorMessage={errors.jobs?.[index]?.driverId?.message}
            />

            <BaseDynamicSelect
              isSearch
              multiple={true}
              gap={8}
              borderRadius={8}
              label={t('menu_apply_settings_label_loader')}
              callApiFn={fetchOptionLoader}
              value={tempSettings?.loaderId || []}
              onChange={(selected) => {
                setTempSettings((prev) => ({
                  ...prev,
                  loaderId: selected?.optionsSelected?.map((opt) => opt.value),
                }));
              }}
              itemNameDefault={orderSplit?.jobs[index]?.loader?.map((item) =>
                getFullName({
                  firstName: item.firstName,
                  lastName: item.lastName,
                })
              )}
              errorMessage={errors.jobs?.[index]?.loaderId?.message}
            />

            <BaseSelect
              isSearch
              type='loadMore'
              gap={8}
              height={37}
              borderRadius={8}
              styleType='contained'
              value={tempSettings?.truckId}
              onChange={(data) => {
                setTempSettings((prev) => ({
                  ...prev,
                  truckId: String(data?.value),
                  trailerType: data?.trailerType,
                }));
              }}
              label={t('menu_apply_settings_label_truck')}
              callApiFn={fetchOptionTruck}
              itemNameDefault={orderSplit?.jobs[index]?.truck?.name}
              errorMessage={errors.jobs?.[index]?.truckId?.message}
            />

            <BaseSelect
              isSearch
              type='loadMore'
              gap={8}
              height={37}
              borderRadius={8}
              value={tempSettings?.trailerId}
              onChange={(data) => {
                setTempSettings((prev) => ({
                  ...prev,
                  trailerId: String(data?.value),
                }));
              }}
              styleType='contained'
              label={t('menu_apply_settings_label_trailer')}
              itemNameDefault={orderSplit?.jobs[index]?.trailer?.name}
              callApiFn={fetchOptionTrailer}
              errorMessage={errors.jobs?.[index]?.trailerId?.message}
            />
          </Box>

          <Box className={cx('menuFooter')}>
            <Button variant='contained' className={cx('buttonApply')} type='button' onClick={handleApplySettings}>
              {t('menu_apply_settings_button_apply')}
            </Button>

            <Button variant='outlined' className={cx('buttonClear')} onClick={handleClear}>
              {t('menu_apply_settings_button_clear')}
            </Button>
          </Box>
        </Box>
      </Popover>

      <Stack direction={'column'} gap={'5px'} className={cx('itemWrap')}>
        {children}
      </Stack>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Stack>
  );
};

export default CustomHeaderColumn;
